import { inject } from '@angular/core';
import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { Observable, switchMap, catchError, from, EMPTY } from 'rxjs';

import { environment } from '../../environments/environment';
import { loginRequest } from './auth-config';
import { CiamAuthService, SUPPORT_RAIL_KEY } from './ciam-auth.service';

// Same-origin as the support API? A raw startsWith(apiBaseUrl) prefix check is a
// token-leak vector: `https://<apiBaseUrl>.evil.example/…` shares the string prefix
// and would receive the bearer token (codex-review HIGH, 2026-08-10). Compare parsed
// ORIGINS instead. Relative URLs resolve against the app origin (never the API), so
// they correctly do NOT match and carry no token.
function isSupportApiRequest(url: string): boolean {
  try {
    return new URL(url, window.location.origin).origin === new URL(environment.apiBaseUrl).origin;
  } catch {
    return false;
  }
}

// Workforce (staff) rail token attach — the original behavior, unchanged. Prefer the
// active account; fall back to a cached account ONLY when there is EXACTLY ONE — never
// pick arbitrarily among multiple, which could acquire under the wrong identity. On an
// interaction-required failure, RE-AUTH via redirect rather than firing token-less.
function attachWorkforceToken(req: HttpRequest<unknown>, next: HttpHandlerFn, msal: MsalService): Observable<HttpEvent<unknown>> {
  const cached = msal.instance.getAllAccounts();
  const account = msal.instance.getActiveAccount() ?? (cached.length === 1 ? cached[0] : null);
  if (!account) {
    return next(req);
  }
  return msal.acquireTokenSilent({ ...loginRequest, account }).pipe(
    switchMap((result) => next(req.clone({ setHeaders: { Authorization: `Bearer ${result.accessToken}` } }))),
    catchError((err) => {
      if (err instanceof InteractionRequiredAuthError) {
        msal.instance.acquireTokenRedirect({ ...loginRequest, account }).catch(() => {});
        return EMPTY;
      }
      return next(req);
    }),
  );
}

// Partner (VAR) rail token attach — the CIAM instance's token, whose aud matches the
// support API. Mirrors the workforce contract: interaction-required → full re-auth via
// the CIAM login redirect (CiamAuthService has no standalone acquireTokenRedirect, and
// loginRedirect re-establishes both session and token); any other failure (including no
// resolvable account) falls through unauthenticated, exactly as the workforce path does.
function attachPartnerToken(req: HttpRequest<unknown>, next: HttpHandlerFn, ciam: CiamAuthService): Observable<HttpEvent<unknown>> {
  return from(ciam.acquireToken()).pipe(
    switchMap((result) => next(req.clone({ setHeaders: { Authorization: `Bearer ${result.accessToken}` } }))),
    catchError((err) => {
      if (err instanceof InteractionRequiredAuthError) {
        ciam.loginRedirect();
        return EMPTY;
      }
      return next(req);
    }),
  );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isSupportApiRequest(req.url)) {
    return next(req);
  }
  // The rail chosen at login (persisted in sessionStorage) decides which instance's
  // token to attach. Staff (and any load with no rail set) → workforce; partner → CIAM.
  const rail = sessionStorage.getItem(SUPPORT_RAIL_KEY);
  if (rail === 'partner') {
    return attachPartnerToken(req, next, inject(CiamAuthService));
  }
  return attachWorkforceToken(req, next, inject(MsalService));
};
